import React, { useRef } from "react";
import { Drawer, Row, Col, Space, Tag, Divider, Button, Alert, Tooltip } from "antd";
import { Link } from "react-router-dom";
import { PrinterOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import moment from "moment-jalaali";
import { useReactToPrint } from "react-to-print";
import SafeHtml from "@general/safeHtml";
import PrintPaper from "@general/printPaper";
import {
  SERVICE_TYPES,
  SERVICE_STATUSES,
  getRepurchaseLink,
  isRenewalDisabled,
  getRenewalDisabledReason,
} from "@staticData/services";

function UserServiceDetailsDrawer({ open, onClose, service, windowSize }) {
  const contentRef = useRef(null);

  const reactToPrintFn = useReactToPrint({
    contentRef,
    pageStyle: `
      @font-face {
        font-family: "IRANSansXFaNum";
        src: url("${window.location.origin}/fonts/woff2/IRANSansXFaNum-Regular.woff2") format("woff2");
      }
      .contract-wrapper,
      .contract-wrapper * {
        font-family: "IRANSansXFaNum" !important;
      }
    `,
  });

  if (!service) return null;

  const {
    serviceName,
    serviceType,
    status,
    amount,
    createdAt,
    startAt,
    endAt,
    description,
    contract,
    packageName,
    packageDays,
    packageSessions,
    meetingRoomName,
    reserve,
    workAt,
    startTime,
    endTime,
    capacity,
  } = service;

  const statusConfig = SERVICE_STATUSES[status] || {
    label: status,
    color: "default",
  };
  const typeLabel = SERVICE_TYPES[serviceType] || serviceType;
  const repurchaseUrl = getRepurchaseLink(service);

  const formatDate = (date, withTime = false) => {
    if (!date) return "-";
    return withTime
      ? moment(date).format("jYYYY/jMM/jDD | HH:mm")
      : moment(date).format("jYYYY/jMM/jDD");
  };

  const isMeetingOrConsultation =
    serviceType === "MEETING_ROOM" || serviceType === "CONSULTATION";

  const renewalDisabled = isRenewalDisabled(service);
  const disabledReason = getRenewalDisabledReason(service);

  const repurchaseButton = (
    <Button
      type="primary"
      icon={<ShoppingCartOutlined />}
      disabled={renewalDisabled}
    >
      تمدید / خرید مجدد
    </Button>
  );

  return (
    <Drawer
      title="جزییات سرویس"
      placement="left"
      onClose={onClose}
      open={open}
      width={windowSize > 768 ? 640 : "100%"}
    >
      {status === "NEAR_EXPIRY" && (
        <Alert
          message="هشدار انقضا"
          description="این سرویس در آستانه انقضا قرار دارد. در صورت نیاز می‌توانید آن را تمدید کنید."
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Space orientation="horizontal" size="middle" wrap>
            <h3 style={{ margin: 0 }}>{serviceName}</h3>
            <Tag color={statusConfig.color}>{statusConfig.label}</Tag>
            <Tag color="cyan">{typeLabel}</Tag>
          </Space>
        </Col>

        <Col sm={12} xs={24}>
          <Space>
            <span style={{ color: "#888" }}>مبلغ:</span>
            <strong>{amount ? amount.toLocaleString() : 0} ریال</strong>
          </Space>
        </Col>

        <Col sm={12} xs={24}>
          <Space>
            <span style={{ color: "#888" }}>تاریخ خرید:</span>
            <span>{formatDate(createdAt)}</span>
          </Space>
        </Col>

        <Col sm={12} xs={24}>
          <Space>
            <span style={{ color: "#888" }}>تاریخ شروع:</span>
            <span>{formatDate(startAt, isMeetingOrConsultation)}</span>
          </Space>
        </Col>

        <Col sm={12} xs={24}>
          <Space>
            <span style={{ color: "#888" }}>تاریخ پایان:</span>
            <span>{formatDate(endAt, isMeetingOrConsultation)}</span>
          </Space>
        </Col>

        {packageName && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>نام پکیج:</span>
              <span>{packageName}</span>
            </Space>
          </Col>
        )}

        {packageDays !== null && packageDays !== undefined && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>مدت پکیج:</span>
              <span>{packageDays} روز</span>
            </Space>
          </Col>
        )}

        {packageSessions !== null && packageSessions !== undefined && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>تعداد جلسات:</span>
              <span>{packageSessions} جلسه</span>
            </Space>
          </Col>
        )}

        {meetingRoomName && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>مکان / اتاق جلسه:</span>
              <span>{meetingRoomName}</span>
            </Space>
          </Col>
        )}

        {workAt && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>تاریخ حضور در روز تعطیل:</span>
              <span>{formatDate(workAt)}</span>
            </Space>
          </Col>
        )}

        {(startTime || endTime) && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>ساعت مجاز:</span>
              <span>
                {startTime || "-"} تا {endTime || "-"}
              </span>
            </Space>
          </Col>
        )}

        {capacity !== null && capacity !== undefined && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>ظرفیت:</span>
              <span>{capacity} نفر</span>
            </Space>
          </Col>
        )}

        {reserve && (
          <Col span={24}>
            <Alert
              message="اطلاعات رزرو"
              description={`جلسه تنظیم‌شده: از ${formatDate(
                reserve.startAt,
                true
              )} تا ${formatDate(reserve.endAt, true)}`}
              type="info"
              showIcon
            />
          </Col>
        )}

        {description && (
          <Col span={24}>
            <div style={{ color: "#888", marginBottom: 6 }}>توضیحات:</div>
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "10px 14px",
                borderRadius: 6,
                border: "1px solid #f0f0f0",
                whiteSpace: "pre-line",
              }}
            >
              <SafeHtml content={description} />
            </div>
          </Col>
        )}
      </Row>

      <Divider />

      <Space wrap size="middle">
        {renewalDisabled ? (
          <Tooltip title={disabledReason}>
            <span>{repurchaseButton}</span>
          </Tooltip>
        ) : (
          <Link to={repurchaseUrl}>
            {repurchaseButton}
          </Link>
        )}

        {contract && (
          <>
            <Button
              type="default"
              icon={<PrinterOutlined />}
              onClick={reactToPrintFn}
            >
              چاپ تعهدنامه / قرارداد
            </Button>
            <div style={{ display: "none" }}>
              <PrintPaper html={contract} ref={contentRef}>
                <span>
                  ثبت شده در تاریخ {moment(createdAt).format("jYYYY/jMM/jDD")}
                </span>
              </PrintPaper>
            </div>
          </>
        )}
      </Space>
    </Drawer>
  );
}

export default UserServiceDetailsDrawer;
